import { type BundleQuestionAnswerFragment, isNative, match } from "@stepapp/core";
import {
  AttributeType,
  NumericType,
  type OrderAttributePayload,
  type V2CartQuestionsUpsert,
} from "@stepapp/core/apiTypes";
import {
  Block,
  Image,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
  useBreakpoints,
} from "@stepapp/ui";
import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { StyleSheet, View } from "react-native";
import type { ReservationFormParams } from "../../store/draftReservation/types";
import OptionRibbon from "./OptionRibbon";
import QuantityActions from "./QuantityActions";

type Params = {
  questionId: string;
  categoryId: string;
  name: string;
  description?: Maybe<string>;
  additionalDescription?: Maybe<string>;
  iconUrl?: string;
  attributes: (OrderAttributePayload | V2CartQuestionsUpsert)[];
  isFeatured?: boolean;
  questionType?: AttributeType;
  numericType?: NumericType | null;
  answers: BundleQuestionAnswerFragment[];
  setModalVisible: ({
    isVisible,
    categoryId,
    questionId,
  }: {
    isVisible: boolean;
    categoryId: string;
    questionId: string;
  }) => void;
};

const CategoryQuestionItem = ({
  name,
  description,
  additionalDescription,
  questionId,
  categoryId,
  iconUrl,
  attributes,
  questionType,
  numericType,
  answers,
  isFeatured = false,
  setModalVisible,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TODO
}: Params) => {
  const { mobileOnly } = useBreakpoints();

  const hasSingleAnswer = answers?.length === 1;
  const isNumericQuestion = questionType === AttributeType.Numeric;
  const isSelected = !!attributes?.find((a) => a?.attributeId === questionId);

  const singleAnswerId = answers[0]?.id || "";
  const currentAnswer = attributes?.find((a) => a?.valueId?.includes(singleAnswerId));

  const isNumericWithOneAnswer = isNumericQuestion && hasSingleAnswer;

  const { setValue } = useFormContext<ReservationFormParams>();

  const handleShowModal = useCallback(() => {
    setModalVisible({
      isVisible: true,
      categoryId,
      questionId,
    });
  }, [setModalVisible, questionId, categoryId]);

  const handlePress = useCallback(() => {
    if (hasSingleAnswer) {
      const filteredQuestions =
        attributes?.filter((attribute) => attribute?.attributeId !== questionId) || [];

      const newAttributes = match(isSelected)
        .with(true, () => filteredQuestions)
        .otherwise(() => [
          ...filteredQuestions,
          {
            attributeId: questionId,
            valueId: [singleAnswerId],
            quantity: 1,
          },
        ]);

      setValue?.("attributes", newAttributes, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      return;
    }
    handleShowModal();
  }, [
    hasSingleAnswer,
    setValue,
    handleShowModal,
    isSelected,
    attributes,
    singleAnswerId,
    questionId,
  ]);

  const handleSelectQuantity = useCallback(
    (quantity: number) => {
      const filteredAnswers =
        attributes?.filter((a) => !a?.valueId?.includes(singleAnswerId)) || [];

      match(quantity)
        .with(0, () => setValue?.("attributes", filteredAnswers))
        .otherwise(() => {
          setValue?.(
            "attributes",
            filteredAnswers.concat([
              { attributeId: questionId, valueId: [singleAnswerId], quantity },
            ]),
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            },
          );
        });
    },
    [attributes, questionId, singleAnswerId, setValue],
  );

  const size = mobileOnly ? 4.7 : 4.5;

  const Wrapper = isNumericWithOneAnswer ? View : Touchable;

  return (
    <Wrapper onPress={handlePress}>
      <Block
        justifyCenter
        marginVertical={isNative ? 1 : 0}
        paddingHorizontal={1.5}
        style={[
          styles.content,
          isSelected && (isNative ? styles.selectedWithoutShadow : styles.selected),
        ]}
      >
        <Block alignCenter row>
          {iconUrl && (
            <Block alignCenter justifyCenter style={styles.questionIcon}>
              <Image height={spacing.unit * 5} source={iconUrl} width={spacing.unit * 5} />
            </Block>
          )}

          <Block justifyCenter flex={1} marginRight={2.5}>
            <Typography
              ellipsizeMode="tail"
              numberOfLines={1}
              type={isSelected ? "white" : "primary"}
              variant="answerTitle"
            >
              {name}
            </Typography>

            {!!description && (
              <Block marginTop={0.5}>
                <Typography
                  ellipsizeMode="tail"
                  numberOfLines={1}
                  type={isSelected ? "white" : "secondary"}
                  variant="paragraph"
                >
                  {description}
                </Typography>
              </Block>
            )}
            {!!additionalDescription && (
              <Block marginTop={0.5}>
                <Typography
                  ellipsizeMode="tail"
                  numberOfLines={1}
                  type={isSelected ? "white" : "secondary"}
                  variant="lightLabel"
                >
                  {additionalDescription}
                </Typography>
              </Block>
            )}
          </Block>

          {isNumericWithOneAnswer ? (
            <QuantityActions
              onChange={handleSelectQuantity}
              quantity={currentAnswer?.quantity || 0}
              singleOption={true}
              numericType={numericType}
              textColor="white"
            />
          ) : isSelected ? (
            <Block
              alignCenter
              justifyCenter
              height={size}
              style={[styles.icon, { backgroundColor: palette.primary.pinkSalmon }]}
              width={size}
            >
              <SvgIcon color={palette.primary.accent} name="edit" size={18} />
            </Block>
          ) : (
            <Block
              alignCenter
              justifyCenter
              height={size}
              style={[styles.icon, { backgroundColor: palette.common.mediumGray }]}
              width={size}
            >
              <SvgIcon color={palette.common.lightGray} name="plus" size={mobileOnly ? 36 : 32} />
            </Block>
          )}
        </Block>
        {isFeatured && <OptionRibbon />}
      </Block>
    </Wrapper>
  );
};

export default React.memo(CategoryQuestionItem);

const styles = StyleSheet.create({
  actionsContainer: {
    paddingBottom: spacing.unit * 4,
    padding: spacing.unit * 2.5,
  },
  content: {
    backgroundColor: palette.common.lightGray,
    borderRadius: spacing.radius,
    overflow: "hidden",
    paddingVertical: spacing.unit * 2,
  },
  icon: {
    borderRadius: spacing.radius * 2,
  },
  selected: {
    backgroundColor: palette.primary.accent,
    boxShadow:
      "0px 100px 80px rgba(0, 0, 84, 0.07), 0px 41.7776px 33.4221px rgba(0, 0, 84, 0.0503198), 0px 22.3363px 17.869px rgba(0, 0, 84, 0.0417275), 0px 12.5216px 10.0172px rgba(0, 0, 84, 0.035), 0px 6.6501px 5.32008px rgba(0, 0, 84, 0.0282725), 0px 2.76726px 2.21381px rgba(0, 0, 84, 0.0196802)",
  },
  selectedWithoutShadow: {
    backgroundColor: palette.primary.accent,
  },
  questionIcon: {
    marginRight: spacing.unit,
  },
});
